import * as Cesium from 'cesium';
import EngineBase, { IOptions } from './EngineBase';
import { Layer } from '@/declarations/layer';
import markerIcon from '@/assets/images/marker-icon-2x.png';

export default class CesiumEngine extends EngineBase {
  setBaseMap(url: string): void {
    console.log('setBaseMap', url);
  }

  createLayer(layer) {
    let newLayer;
    if (layer.type == 'wmts') {
      newLayer = new Cesium.WebMapTileServiceImageryProvider({
        url: layer.url,
        layer: layer.options.layer,
        style: layer.options.style,
        format: layer.options.format,
        tileMatrixSetID: layer.options.tileMatrixSetID,
        maximumLevel: 18,
        subdomains: layer.options.subdomains,
      });
    } else if (layer.type == 'wms') {
      newLayer = new Cesium.WebMapServiceImageryProvider({
        url: layer.url,
        layers: layer.options.layers,
        parameters: layer.options.parameters,
      });
    } else {
      newLayer = new Cesium.UrlTemplateImageryProvider({
        url: layer.url,
        tilingScheme: new Cesium.WebMercatorTilingScheme(),
        maximumLevel: 18,
      });
    }
    return newLayer;
  }

  getReverseIndex(index: number) {
    return this.layers.length - index - 1;
  }

  addLayer(layer: Layer): void {
    // 检查是否已经添加过该图层
    if (this.layers.includes(layer.uid)) {
      return;
    }
    this.layers.push(layer.uid);    
    this.engine.imageryLayers.addImageryProvider(this.createLayer(layer));
    if (layer.options && layer.options.bounds) {
      const bounds = layer.options.bounds;
      this.setBBox([bounds[0][1], bounds[0][0], bounds[1][1], bounds[1][0]])
    }
  }

  removeLayer(uid: string): void {
    const index = this.layers.indexOf(uid);
    if (index === -1) {
      return;
    }
    // index + 1 排除底图
    this.engine.imageryLayers.remove(this.engine.imageryLayers.get(index + 1));
    this.layers.splice(index, 1);
  }

  refreshLayer(layer: Layer): void {
    const index = this.layers.indexOf(layer.uid);
    if (index === -1) {
      return;
    }
    const tmsLayer = this.engine.imageryLayers.get(index + 1);
    const newTmsLayer = new Cesium.UrlTemplateImageryProvider({
      url: layer.url,
      tilingScheme: new Cesium.WebMercatorTilingScheme(),
      maximumLevel: 18,
    });
    this.engine.imageryLayers.remove(tmsLayer);
    this.engine.imageryLayers.addImageryProvider(newTmsLayer, index + 1);
  }

  updateLayer(index: number, key: keyof IOptions, val: number): void {
    const rindex = this.getReverseIndex(index);
    const layer = this.engine.imageryLayers.get(rindex + 1);

    if (layer) {
      layer[key] = val;
    }
  }

  moveLayer(from: number, to: number): void {
    const rfrom = this.getReverseIndex(from);
    const rto = this.getReverseIndex(to);
    const fromLayer = this.engine.imageryLayers.get(rfrom + 1);
    this.engine.imageryLayers.remove(fromLayer, false);
    this.engine.imageryLayers.add(fromLayer, rto + 1);
    const fromUid = this.layers[rfrom];
    this.layers.splice(rfrom, 1);
    this.layers.splice(rto, 0, fromUid);
  }

  hideLayer(index: number): void {
    const rindex = this.getReverseIndex(index);
    this.engine.imageryLayers.get(rindex + 1).show = false;
  }

  showLayer(index: number): void {
    const rindex = this.getReverseIndex(index);
    this.engine.imageryLayers.get(rindex + 1).show = true;
  }

  setCenter(lng: number, lat: number): void {
    this.engine.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(
        lng,
        lat,
        this.engine.camera.positionCartographic.height,
      ),
    });
  }

  setBBox(bbox: number[]): void {
    this.engine.camera.flyTo({
      destination: Cesium.Rectangle.fromDegrees(
        bbox[0],
        bbox[1],
        bbox[2],
        bbox[3],
      ),
    });
  }

  showCenterMarker(bbox: number[]): void {
    if (this.centerMaker) {
      this.hideCenterMarker();
    }

    const centerLongitude = (bbox[0] + bbox[2]) / 2;
    const centerLatitude = (bbox[1] + bbox[3]) / 2;

    const viewer = this.engine;
    const marker = new Cesium.Entity({
      id: 'marker',
      position: Cesium.Cartesian3.fromDegrees(centerLongitude, centerLatitude),
      billboard: {
        image: markerIcon,
        width: 25,
        height: 41,
      },
      // point: {
      //   pixelSize: 10,
      //   color: Cesium.Color.RED,
      // }
    });
    viewer.entities.add(marker);
    this.centerMaker = marker;
  }

  hideCenterMarker(): void {
    if (this.centerMaker) {
      this.engine.entities.remove(this.centerMaker);
      this.centerMaker = null;
    }
  }

  getCenter(): number[] {
    const center = this.engine.camera.positionCartographic;
    return [
      Cesium.Math.toDegrees(center.latitude),
      Cesium.Math.toDegrees(center.longitude),
    ];
  }
}
